import { Controller } from "@hotwired/stimulus"

const REDUCED = "(prefers-reduced-motion: reduce)"

/**
 * Reading comfort, demonstrated on a miniature of the reader: focus mode hides
 * the chrome and widens the measure, then the theme flips to dark and back.
 *
 * The frame carries its own `--paper` and `--ink`, so flipping `is-dark` on it
 * re-themes only the preview and leaves the rest of the page alone.
 */
export default class extends Controller {
  static targets = ["frame", "label"]

  static values = { beat: { type: Number, default: 2100 } }

  connect() {
    if (window.matchMedia(REDUCED).matches) return

    this.cancelled = false
    this.visible = true

    this.observer = new IntersectionObserver(
      ([entry]) => (this.visible = entry.isIntersecting),
      { threshold: 0, rootMargin: "-15% 0px -15% 0px" }
    )
    this.observer.observe(this.element)

    this.run()
  }

  disconnect() {
    this.cancelled = true
    if (this.observer) this.observer.disconnect()
  }

  async run() {
    const steps = [
      { classes: [], label: "reading" },
      { classes: ["is-focus"], label: "focus mode" },
      { classes: ["is-focus", "is-dark"], label: "focus mode · dark" },
      { classes: ["is-dark"], label: "dark" }
    ]

    let i = 0
    while (!this.cancelled) {
      await this.idleUntilVisible()
      if (this.cancelled) return

      const step = steps[i % steps.length]
      this.apply(step)
      await this.sleep(this.beatValue)
      i++
    }
  }

  apply({ classes, label }) {
    if (this.hasFrameTarget) {
      this.frameTarget.classList.toggle("is-focus", classes.includes("is-focus"))
      this.frameTarget.classList.toggle("is-dark", classes.includes("is-dark"))
    }
    if (this.hasLabelTarget) this.labelTarget.textContent = label
  }

  async idleUntilVisible() {
    while (!this.visible && !this.cancelled) await this.sleep(300)
  }

  sleep(ms) {
    return new Promise((resolve) => {
      const id = setTimeout(resolve, ms)
      if (this.cancelled) {
        clearTimeout(id)
        resolve()
      }
    })
  }
}
